import {
  serverTimestamp,
  getDoc,
  Timestamp,
  runTransaction,
  type WithFieldValue,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  writeBatch,
  getFirestore,
  type QueryDocumentSnapshot,
  type UpdateData,
} from 'firebase/firestore'
import { getFirebaseApp } from '../config/firebase.config'
import { getTownFormBaseUrl } from '../config/form-url.config'
import type { FirestoreBaseDoc } from '../models/base/firestore-model-base'
import { refs } from '../models/base/firestore-refs'
import {
  CompanySurveyForm,
  CompanySurveyFormDoc,
  toCompanySurveyForm,
} from '../models/project/company_survey_form_model'
import { type ProjectDoc, toProjectEntity, toProjectSnapshotJson } from '../models/project/project_model'
import type { SchoolAccountDoc } from '../models/account/school/school-account.modl'
import {
  CompanySurveySendList,
  CompanySurveySendListDoc,
  toCompanySurveySendList,
} from '../models/project/company_survey_send_list_model'
import {
  CompanySurveyAnswer,
  CompanySurveyAnswerDoc,
  CompanySurveyAnswerPayload,
  companySurveyAnswerPayloadToDoc,
  toCompanySurveyAnswer,
} from '../models/project/company_survey_answer_model'
import {
  type CompanySurveyAnswerHistoryDoc,
  type CompanySurveyAnswerHistoryEntity,
  toCompanySurveyAnswerHistoryEntity,
} from '../models/project/company_survey_answer_history.modl'
import type { TownCompany } from '../models/town_company/town-company.modl'
import { normalizePostalCodeForStorage } from '../utils/postal-code'
import type { InvoiceItemDoc } from '../models/invoice/invoice-item.modl'
import { buildPostcardInvoiceItems, calcPostcardTotalAmount } from '../models/invoice/invoice-item.modl'
import { getPostcardMinSendCount, getShippingRecipientsRequiredFieldsError, hasEnoughPostcardRecipients, sumTotalRequestedWithoutSendNumber } from '../utils/postcard-shipping'
import {
  createBaseFields,
  getAll,
  getOne,
  updateWithUpdatedAt,
} from './firestore-crud'
import { FormShortUrlService } from './form_short_url_service'

/** フォームへ同期する project_* ペイロードの元になるプロジェクト情報 */
export type ProjectFormSyncSource = {
  name?: string
  description?: string | null
  notes?: string | null
  project_number?: string
  start_date?: Date | null
  end_date?: Date | null
  start_time?: string
  end_time?: string
  min_experience_days?: number
  min_experience_time?: number
  student_grade?: string
  participant_count?: number | null
  min_participants_per_company?: number
  max_participants_per_company?: number
  contact_person_name?: string
  lunch_notes?: string | null
  optional_question_1?: string | null
  optional_question_2?: string | null
  optional_question_3?: string | null
  prior_contact_visit_plan?: string | null
  prior_contact_visit_schedule?: string | null
  school_nearest_station?: string | null
  preferred_route_and_travel_time?: string | null
}

/** 回答更新時の更新者情報（省略時は anon として履歴に記録） */
export type SurveyAnswerUpdatedBy =
  | { type: 'anon' }
  | { type: 'school'; userId: string; name: string; documentPath: string }
  | { type: 'admin'; userId: string; name: string; documentPath: string }

/**
 * 事業所回答フォーム（マスター）と事業所回答の取得・保存を行うサービス
 */
export class CompanySurveyService {
  /**
   * school_accounts から town 画面表示用の学校情報を取得し、
   * company_survey_forms の school_* 形式へ変換する。
   */
  async getSchoolDisplayInfoForForm(
    schoolId: string,
  ): Promise<
    Pick<
      CompanySurveyFormDoc,
      | 'school_name'
      | 'school_principal'
      | 'school_postal_code'
      | 'school_address'
      | 'school_phone'
      | 'school_email'
    >
  > {
    const schoolSnap = await getDoc(refs.schoolAccount(schoolId))
    if (!schoolSnap.exists()) {
      return {
        school_name: null,
        school_principal: null,
        school_postal_code: null,
        school_address: null,
        school_phone: null,
        school_email: null,
      }
    }

    const school = schoolSnap.data() as SchoolAccountDoc
    const schoolAddress = `${school.prefecture ?? ''}${school.city ?? ''}${school.address ?? ''}`.trim()
    const postal = school.postal_code?.trim() || null

    return {
      school_name: school.school_name ?? null,
      school_principal: school.principal ?? null,
      school_postal_code: postal,
      school_address: schoolAddress || null,
      school_phone: school.phone ?? null,
      school_email: school.contact_email ?? school.email ?? null,
    }
  }

  /**
   * 指定 ID の事業所回答フォームを取得する（プロジェクトに紐づくフォームはこのメソッドで取得すること）
   */
  async getForm(
    schoolId: string,
    projectId: string,
    includeDeleted: boolean = false,
  ): Promise<CompanySurveyForm | null> {
    return getOne(
      refs.companySurveyForm(schoolId, projectId),
      (id, data, path) => toCompanySurveyForm(id, data, path),
      { includeDeleted },
    )
  }

  /**
   * 指定 ID の郵送リスト（郵送確定1回分）を取得する。
   */
  async getSendListById(
    schoolId: string,
    projectId: string,
    sendListId: string,
    includeDeleted: boolean = false,
  ): Promise<CompanySurveySendList | null> {
    return getOne(
      refs.companySurveySendList(schoolId, projectId, sendListId),
      (id, data, path) => toCompanySurveySendList(id, data as CompanySurveySendListDoc, path),
      { includeDeleted },
    )
  }

  /**
   * プロジェクト配下の郵送リストを全て取得する。
   */
  async getSendListsForProject(
    schoolId: string,
    projectId: string,
    includeDeleted: boolean = false,
  ): Promise<CompanySurveySendList[]> {
    return getAll(
      refs.companySurveySendLists(schoolId, projectId),
      (id, data, path) => toCompanySurveySendList(id, data as CompanySurveySendListDoc, path),
      { includeDeleted },
    )
  }

  /**
   * プロジェクト作成時にフォームを1件作成する（1プロジェクト1フォーム、id=current）。
   * createProject（管理者用など）から呼ぶ。createProjectAsSchool はトランザクション内で自前でフォームを作成する。
   */
  async createFormForProjectAndLink(
    schoolId: string,
    projectId: string,
    data: {
      name: string
      description?: string
      notes?: string | null
      project_number?: string
      start_date?: Date
      end_date?: Date
      start_time?: string
      end_time?: string
      min_experience_days?: number
      min_experience_time?: number
      student_grade?: string
      participant_count?: number | null
      min_participants_per_company?: number
      max_participants_per_company?: number
      contact_person_name?: string
      lunch_notes?: string | null
      optional_question_1?: string | null
      optional_question_2?: string | null
      optional_question_3?: string | null
      prior_contact_visit_plan?: string | null
      prior_contact_visit_schedule?: string | null
      school_nearest_station?: string | null
      preferred_route_and_travel_time?: string | null
    },
  ): Promise<void> {
    const schoolDisplayInfo = await this.getSchoolDisplayInfoForForm(schoolId)
    const formatYMD = (d: Date | undefined) =>
      d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null
    const formRef = refs.companySurveyForm(schoolId, projectId)
    const projectRef = refs.schoolAccountProject(schoolId, projectId)
    const ts = serverTimestamp() as unknown as Timestamp
    const formPayload: Partial<CompanySurveyFormDoc> = {
      title: data.name ?? '',
      description: data.description ?? '',
      optional_question_1: data.optional_question_1 ?? null,
      optional_question_2: data.optional_question_2 ?? null,
      optional_question_3: data.optional_question_3 ?? null,
      project_number: data.project_number ?? null,
      project_name: data.name ?? null,
      project_start_date: formatYMD(data.start_date) ?? null,
      project_end_date: formatYMD(data.end_date) ?? null,
      project_description: data.description ?? null,
      project_start_time: data.start_time ?? null,
      project_end_time: data.end_time ?? null,
      project_min_experience_days: data.min_experience_days ?? null,
      project_min_experience_time: data.min_experience_time ?? null,
      project_student_grade: data.student_grade ?? null,
      participant_count: data.participant_count ?? null,
      project_min_participants_per_company: data.min_participants_per_company ?? null,
      project_max_participants_per_company: data.max_participants_per_company ?? null,
      project_contact_person_name: data.contact_person_name ?? null,
      project_lunch_notes: data.lunch_notes ?? null,
      project_prior_contact_visit_plan: data.prior_contact_visit_plan ?? null,
      project_prior_contact_visit_schedule: data.prior_contact_visit_schedule ?? null,
      project_school_nearest_station: data.school_nearest_station ?? null,
      project_preferred_route_and_travel_time: data.preferred_route_and_travel_time ?? null,
      ...schoolDisplayInfo,
    }
    await runTransaction(getFirestore(getFirebaseApp()), async (transaction) => {
      transaction.set(formRef, { ...formPayload, ...createBaseFields(false) } as CompanySurveyFormDoc)
      transaction.update(projectRef, { updated_at: ts })
    })
  }

  /**
   * 最新のプロジェクト内容を取得し、事業所回答フォームへ同期する。
   * サンプルページ表示前など、フォーム側の表示を projects と揃えたいときに使う。
   */
  async syncFormFromLatestProject(schoolId: string, projectId: string): Promise<void> {
    const projectSnap = await getDoc(refs.schoolAccountProject(schoolId, projectId))
    if (!projectSnap.exists()) {
      throw new Error('プロジェクトが見つかりません')
    }
    const project = toProjectEntity(projectSnap.id, projectSnap.data() as ProjectDoc, projectSnap.ref.path)
    project.school_account_id = schoolId
    await this.syncFormFromProject(schoolId, projectId, project)
  }

  /**
   * プロジェクト内容から、フォームへ同期する project_* 系のペイロードを組み立てる（純粋関数）。
   * 学校情報（school_*）は含めない。トランザクション内・外どちらからでも利用できる。
   */
  buildProjectFormSyncPayload(project: ProjectFormSyncSource): Partial<CompanySurveyFormDoc> {
    const formatYMD = (d: Date | undefined | null) =>
      d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null
    return {
      title: project.name ?? '',
      description: project.description ?? '',
      optional_question_1: project.optional_question_1 ?? null,
      optional_question_2: project.optional_question_2 ?? null,
      optional_question_3: project.optional_question_3 ?? null,
      project_number: project.project_number ?? null,
      project_name: project.name ?? null,
      project_start_date: formatYMD(project.start_date ?? undefined) ?? null,
      project_end_date: formatYMD(project.end_date ?? undefined) ?? null,
      project_description: project.description ?? null,
      project_start_time: project.start_time ?? null,
      project_end_time: project.end_time ?? null,
      project_min_experience_days: project.min_experience_days ?? null,
      project_min_experience_time: project.min_experience_time ?? null,
      project_student_grade: project.student_grade ?? null,
      participant_count: project.participant_count ?? null,
      project_min_participants_per_company: project.min_participants_per_company ?? null,
      project_max_participants_per_company: project.max_participants_per_company ?? null,
      project_contact_person_name: project.contact_person_name ?? null,
      project_lunch_notes: project.lunch_notes ?? null,
      project_prior_contact_visit_plan: project.prior_contact_visit_plan ?? null,
      project_prior_contact_visit_schedule: project.prior_contact_visit_schedule ?? null,
      project_school_nearest_station: project.school_nearest_station ?? null,
      project_preferred_route_and_travel_time: project.preferred_route_and_travel_time ?? null,
    }
  }

  /**
   * プロジェクトの内容に合わせてフォームを更新する。プロジェクト更新時に呼ぶ。
   * ※ プロジェクト更新と同一トランザクションで同期したい場合は、
   *   getSchoolDisplayInfoForForm + buildProjectFormSyncPayload を使って呼び出し側で書き込むこと。
   */
  async syncFormFromProject(
    schoolId: string,
    projectId: string,
    project: ProjectFormSyncSource,
  ): Promise<void> {
    const schoolDisplayInfo = await this.getSchoolDisplayInfoForForm(schoolId)
    const formRef = refs.companySurveyForm(schoolId, projectId)
    const payload: Partial<CompanySurveyFormDoc> = {
      ...this.buildProjectFormSyncPayload(project),
      ...schoolDisplayInfo,
    }
    await updateWithUpdatedAt(formRef, payload as UpdateData<CompanySurveyFormDoc>)
  }

  /**
   * 事業所回答フォームのログイン画面URLを取得する（短縮URL優先）。
   * ベースURLは環境変数 VITE_TOWN_FORM_BASE_URL または TOWN_FORM_BASE_URL で指定。
   * 短縮コードがあれば domain/s/xxxx、なければ domain/login/schoolId/projectId を返す。
   */
  async getFormUrl(schoolId: string, projectId: string): Promise<string> {
    const base = getTownFormBaseUrl().replace(/\/$/, '')
    if (!base) return ''
    const code = await new FormShortUrlService().getShortCode(schoolId, projectId)
    if (code) return `${base}/s/${code}`
    return `${base}/login/${schoolId}/${projectId}`
  }

  /**
   * サンプル事業所回答フォームのURLを取得する（managementId=sample のダミー表示）。
   */
  getSampleFormUrl(schoolId: string, projectId: string): string {
    const base = getTownFormBaseUrl().replace(/\/$/, '')
    return base ? `${base}/response/${schoolId}/${projectId}/sample` : ''
  }

  /**
   * 既存の事業所回答フォームの表示用情報（タイトル・学校・プロジェクト）のみを更新する。
   * 郵送対象や回答データは変更しない。
   */
  async updateFormDisplayInfo(
    schoolId: string,
    projectId: string,
    options: {
      title?: string
      displayInfo?: {
        school?: {
          name: string
          principal?: string
          contactPersonName?: string
          postalCode?: string
          address: string
          phone: string
          email: string
        }
        project?: {
          projectNumber?: string
          name: string
          startDate?: Date
          endDate?: Date
          description?: string
          participantCount?: number
        }
      }
    },
  ): Promise<void> {
    const formRef = refs.companySurveyForm(schoolId, projectId)
    const disp = options.displayInfo
    const formatYMD = (d: Date | undefined) =>
      d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null
    const payload: Partial<CompanySurveyFormDoc> = {
      ...(options.title !== undefined && { title: options.title }),
      ...(disp?.school && {
        school_name: disp.school.name ?? null,
        school_principal: disp.school.principal ?? null,
        school_postal_code: disp.school.postalCode?.trim() || null,
        school_address: disp.school.address ?? null,
        school_phone: disp.school.phone ?? null,
        school_email: disp.school.email ?? null,
      }),
      ...(disp?.project && {
        project_number: disp.project.projectNumber ?? null,
        project_name: disp.project.name ?? null,
        project_start_date: formatYMD(disp.project.startDate) ?? null,
        project_end_date: formatYMD(disp.project.endDate) ?? null,
        project_description: disp.project.description ?? null,
        participant_count: disp.project.participantCount ?? null,
      }),
    }
    await updateWithUpdatedAt(formRef, payload as UpdateData<CompanySurveyFormDoc>)
  }

  /**
   * フォームの学校情報（school_*）を school_accounts の内容で最新化する。
   */
  async syncFormSchoolInfoFromSchoolAccount(schoolId: string, projectId: string): Promise<void> {
    const schoolInfo = await this.getSchoolDisplayInfoForForm(schoolId)
    const formRef = refs.companySurveyForm(schoolId, projectId)
    await updateWithUpdatedAt(
      formRef,
      schoolInfo as UpdateData<CompanySurveyFormDoc>,
    )
  }

  /**
   * はがきサンプル用に、プロジェクトに紐づくフォームのログインURLを取得する。
   * フォームが存在しない場合は null。
   */
  async getFormUrlForPostcard(schoolId: string, projectId: string): Promise<string | null> {
    const form = await this.getForm(schoolId, projectId)
    if (!form) return null
    return this.getFormUrl(schoolId, projectId) || null
  }
  /**
   * 事業所の回答を取得する
   */
  async getAnswer(
    schoolId: string,
    projectId: string,
    townId: string,
    includeDeleted: boolean = false,
  ): Promise<CompanySurveyAnswer | null> {
    return getOne(
      refs.companySurveyAnswer(schoolId, projectId, townId),
      (id, data, path) => toCompanySurveyAnswer(id, data, path),
      { includeDeleted },
    )
  }

  /**
   * 事業所の回答を保存し、履歴に1件追加する。
   * - anon: updatedBy 省略で「anon」として履歴に記録（追加のみ可能で読み取りは不可）
   * - school / admin: updatedBy を渡すと履歴に更新者を記録し、getAnswerHistory で読み取り可
   */
  async submitAnswer(
    schoolId: string,
    projectId: string,
    townId: string,
    data: CompanySurveyAnswerPayload,
    updatedBy?: SurveyAnswerUpdatedBy,
  ): Promise<void> {
    const answerRef = refs.companySurveyAnswer(schoolId, projectId, townId)
    const historyColRef = refs.companySurveyAnswerHistories(schoolId, projectId, townId)
    const db = getFirestore(getFirebaseApp())

    const ts = serverTimestamp() as unknown as Timestamp
    const payload: Partial<CompanySurveyAnswerDoc> = {
      ...companySurveyAnswerPayloadToDoc(data),
      submitted_at: ts,
      updated_at: ts as unknown as CompanySurveyAnswerDoc['updated_at'],
      is_deleted: false,
    }

    await runTransaction(db, async (transaction) => {
      const existingSnap = await transaction.get(answerRef)
      const existed = existingSnap.exists()
      const existingData = (existingSnap.data() ?? {}) as Partial<CompanySurveyAnswerDoc>

      const mergedDoc: Partial<CompanySurveyAnswerDoc> = {
        ...existingData,
        ...payload,
        ...(existed
          ? {}
          : { created_at: ts as unknown as CompanySurveyAnswerDoc['created_at'] }),
      }

      transaction.set(answerRef, mergedDoc as CompanySurveyAnswerDoc, { merge: true })

      const by = updatedBy ?? { type: 'anon' as const }
      const historyPayload: CompanySurveyAnswerHistoryDoc = {
        answer: mergedDoc as CompanySurveyAnswerDoc,
        updated_by_user_type: by.type,
        updated_by_user_id: by.type === 'anon' ? null : by.userId,
        updated_by_name: by.type === 'anon' ? null : by.name,
        updated_by_user_document_path: by.type === 'anon' ? null : by.documentPath,
        update_info: by.type === 'anon' ? '事業所による回答送信' : '回答の更新',
        created_at: serverTimestamp() as unknown as FirestoreBaseDoc['created_at'],
        updated_at: serverTimestamp() as unknown as FirestoreBaseDoc['updated_at'],
        is_deleted: false,
      }
      const historyDocRef = doc(historyColRef)
      transaction.set(historyDocRef, historyPayload)
    })
  }

  /**
   * 事業所回答の郵便番号・住所だけを更新し、履歴に1件追加する。
   * 他の回答項目は変更しない（事業所データ town_companies も更新しない）。
   */
  async updateAnswerAddress(
    schoolId: string,
    projectId: string,
    townId: string,
    data: { postalCode: string; address: string },
    updatedBy?: SurveyAnswerUpdatedBy,
  ): Promise<void> {
    const answerRef = refs.companySurveyAnswer(schoolId, projectId, townId)
    const historyColRef = refs.companySurveyAnswerHistories(schoolId, projectId, townId)
    const db = getFirestore(getFirebaseApp())
    const ts = serverTimestamp() as unknown as Timestamp
    const postalCode = normalizePostalCodeForStorage(data.postalCode)
    const address = data.address.trim()

    await runTransaction(db, async (transaction) => {
      const existingSnap = await transaction.get(answerRef)
      const existed = existingSnap.exists()
      const existingData = (existingSnap.data() ?? {}) as Partial<CompanySurveyAnswerDoc>
      const mergedDoc: Partial<CompanySurveyAnswerDoc> = {
        ...existingData,
        postal_code: postalCode || null,
        address: address || null,
        updated_at: ts as unknown as CompanySurveyAnswerDoc['updated_at'],
        is_deleted: false,
        ...(existed
          ? {}
          : { created_at: ts as unknown as CompanySurveyAnswerDoc['created_at'] }),
      }

      transaction.set(answerRef, mergedDoc as CompanySurveyAnswerDoc, { merge: true })

      const by = updatedBy ?? { type: 'anon' as const }
      const historyPayload: CompanySurveyAnswerHistoryDoc = {
        answer: mergedDoc as CompanySurveyAnswerDoc,
        updated_by_user_type: by.type,
        updated_by_user_id: by.type === 'anon' ? null : by.userId,
        updated_by_name: by.type === 'anon' ? null : by.name,
        updated_by_user_document_path: by.type === 'anon' ? null : by.documentPath,
        update_info: '郵便番号・住所の更新',
        created_at: serverTimestamp() as unknown as FirestoreBaseDoc['created_at'],
        updated_at: serverTimestamp() as unknown as FirestoreBaseDoc['updated_at'],
        is_deleted: false,
      }
      transaction.set(doc(historyColRef), historyPayload)
    })
  }

  /**
   * 事業所回答の履歴を取得する（school / admin 用。anon は権限上読み取り不可）
   */
  async getAnswerHistory(
    schoolId: string,
    projectId: string,
    townCompanyId: string,
    includeDeleted: boolean = false,
  ): Promise<CompanySurveyAnswerHistoryEntity[]> {
    const historyColRef = refs.companySurveyAnswerHistories(schoolId, projectId, townCompanyId)
    const q = query(
      historyColRef,
      orderBy('created_at', 'desc'),
    )
    const snap = await getDocs(q)
    const list = snap.docs
      .map((d) => {
        const data = d.data()
        if (!includeDeleted && (data as { is_deleted?: boolean })?.is_deleted) return null
        return toCompanySurveyAnswerHistoryEntity(d.id, data as CompanySurveyAnswerHistoryDoc, d.ref.path)
      })
      .filter((x): x is NonNullable<typeof x> => x != null)
    return list
  }

  /**
   * 複数の事業所データについて、事業所回答を一括取得する。
   * @returns townCompanyId -> CompanySurveyAnswer の Map（存在しない回答は含まない）
   */
  async getAnswersForTownCompanies(
    schoolId: string,
    projectId: string,
    townCompanyIds: string[],
  ): Promise<Map<string, CompanySurveyAnswer>> {
    const results = await Promise.all(
      townCompanyIds.map(async (id) => {
        const answer = await this.getAnswer(schoolId, projectId, id)
        return [id, answer] as const
      }),
    )
    const map = new Map<string, CompanySurveyAnswer>()
    for (const [id, answer] of results) {
      if (answer) map.set(id, answer)
    }
    return map
  }

  /**
   * 郵送回が未設定のリクエストの requested_count 合計を取得する。
   * はがき郵送の最低件数判定（hasEnoughPostcardRecipients）の第1引数に使う。
   */
  async getTotalRequestedWithoutSendNumber(schoolId: string, projectId: string): Promise<number> {
    const requestsRef = refs.addTownCompanyRequests(schoolId, projectId)
    const requestsSnap = await getDocs(
      query(requestsRef, where('is_deleted', '==', false)),
    )
    return sumTotalRequestedWithoutSendNumber(
      requestsSnap.docs.map((d) => {
        const data = d.data() as {
          send_request_number?: string | null
          status?: string | null
          requested_count?: number
        }
        return {
          requestedCount: data.requested_count ?? 0,
          status: data.status ?? '',
          sendRequestNumber: data.send_request_number,
        }
      }),
    )
  }

  /**
   * 郵送対象を確定し、郵送リスト（CompanySurveySendListDoc）を1件作成する。
   * 併せてフォームの学校表示情報（school_*）を最新化する。
   * フォームが存在しない場合はエラー。
   */
  async confirmSendListAndForm(
    schoolId: string,
    projectId: string,
    formData: {
      /** 郵送回（DBに保存する） */
      requestNumber: string
      /** 回答期限 */
      deadline: Date
      closed?: boolean
      /** 以下は郵送リストには不要（互換のため受け取りのみ） */
      title?: string
      description?: string
      displayInfo?: {
        school?: { name: string; principal?: string; address: string; phone: string; email: string }
        project?: {
          projectNumber?: string
          name: string
          startDate?: Date
          endDate?: Date
          description?: string
          participantCount?: number
        }
      }
      /** クライアントで表示したはがき郵送費（不達控除後）。渡された場合はサーバー計算値と突合し不一致ならエラー */
      expectedPostcardTotalAmount?: number
    },
    townCompanyLists: TownCompany[],
  ): Promise<void> {
    const form = await this.getForm(schoolId, projectId)
    if (!form) {
      throw new Error('フォームが存在しません。プロジェクト作成時にフォームが作成されます。')
    }

    const serverPostcardTotal = calcPostcardTotalAmount(townCompanyLists.length)
    if (
      formData.expectedPostcardTotalAmount != null &&
      formData.expectedPostcardTotalAmount !== serverPostcardTotal
    ) {
      throw new Error(
        '請求金額の検証に失敗しました。画面を更新して再度お試しください。',
      )
    }

    const totalRequestedWithoutSendNumber = await this.getTotalRequestedWithoutSendNumber(schoolId, projectId)
    if (!hasEnoughPostcardRecipients(totalRequestedWithoutSendNumber, townCompanyLists.length)) {
      throw new Error(
        `郵送対象数（${townCompanyLists.length}件）が最低郵送件数（${getPostcardMinSendCount(totalRequestedWithoutSendNumber)}件）に達していないため、データを確定して郵送できません。`,
      )
    }

    const requiredFieldsError = getShippingRecipientsRequiredFieldsError(townCompanyLists)
    if (requiredFieldsError) {
      throw new Error(requiredFieldsError)
    }

    const sendTargets = townCompanyLists.map((tc) => ({
      management_id: tc.managementId,
      document_id: tc.id,
    }))
    const sendListsColRef = refs.companySurveySendLists(schoolId, projectId)
    const projectRef = refs.schoolAccountProject(schoolId, projectId)
    const invoiceItemsColRef = refs.schoolAccountInvoiceItems(schoolId, projectId)
    const db = getFirestore(getFirebaseApp())

    await runTransaction(db, async (transaction) => {
      const ts = serverTimestamp() as unknown as Timestamp
      const tsAnswer = serverTimestamp() as unknown as Timestamp

      // トランザクションではすべての read を write より前に実行する必要がある
      const projectSnap = await transaction.get(projectRef)
      if (!projectSnap.exists()) {
        throw new Error('プロジェクトが見つかりません')
      }
      const schoolAccountSnap = await transaction.get(refs.schoolAccount(schoolId))
      const isTestSchool =
        schoolAccountSnap.exists() &&
        (schoolAccountSnap.data() as SchoolAccountDoc).is_test_school === true
      const currentProject = toProjectEntity(
        projectSnap.id,
        projectSnap.data() as ProjectDoc,
        projectSnap.ref.path,
      )
      currentProject.school_account_id = schoolId
      const answerSnaps = await Promise.all(
        townCompanyLists.map((tc) =>
          transaction.get(refs.companySurveyAnswer(schoolId, projectId, tc.id)),
        ),
      )

      const deadlineTs = Timestamp.fromDate(formData.deadline)
      const formRef = refs.companySurveyForm(schoolId, projectId)
      transaction.update(formRef, {
        deadline: deadlineTs,
        closed: formData.closed ?? false,
        updated_at: ts,
      } as UpdateData<CompanySurveyFormDoc>)
      const sendListRef = doc(sendListsColRef)
      const sendListPayload: Partial<CompanySurveySendListDoc> = {
        request_number: formData.requestNumber,
        send_targets: sendTargets,
      }
      transaction.set(sendListRef, { ...sendListPayload, ...createBaseFields(false) } as CompanySurveySendListDoc)

      townCompanyLists.forEach((tc, i) => {
        const answerRef = refs.companySurveyAnswer(schoolId, projectId, tc.id)
        const answerExisted = answerSnaps[i].exists()
        const existingData = (answerExisted ? answerSnaps[i].data() : {}) as Partial<CompanySurveyAnswerDoc>
        const prefillPayload: Partial<CompanySurveyAnswerDoc> = {
          ...existingData,
          management_id: tc.managementId ?? null,
          company_name: tc.companyName ?? null,
          postal_code: tc.postalCode ? normalizePostalCodeForStorage(tc.postalCode) : null,
          address: tc.address ?? null,
          phone: tc.phone ?? null,
          request_number: tc.sendRequestNumber ?? null,
          updated_at: tsAnswer as unknown as CompanySurveyAnswerDoc['updated_at'],
          is_deleted: false,
          ...(answerExisted
            ? {}
            : {
                created_at: tsAnswer as unknown as CompanySurveyAnswerDoc['created_at'],
              }),
        }
        transaction.set(answerRef, prefillPayload as CompanySurveyAnswerDoc, { merge: true })
      })

      // 郵送対象の town company の send_status を sent に、はがき郵送回を保存
      townCompanyLists.forEach((tc) => {
        const townCompanyRef = refs.schoolAccountTownCompany(schoolId, projectId, tc.id)
        transaction.update(townCompanyRef, {
          send_status: 'sent',
          send_request_number: formData.requestNumber ?? null,
          updated_at: ts,
        })
      })

      // 利用明細（はがき郵送費・控除額）を Project 配下に作成（テスト学校は請求データを作らない）
      if (!isTestSchool) {
        buildPostcardInvoiceItems(townCompanyLists.length, formData.requestNumber).forEach((invoiceItem) => {
          const invoiceItemRef = doc(invoiceItemsColRef)
          transaction.set(
            invoiceItemRef,
            {
              ...invoiceItem,
              ...createBaseFields(false),
            } as WithFieldValue<InvoiceItemDoc>,
          )
        })
      }

      transaction.update(projectRef, {
        status: 'preparing_shipment',
        updated_at: ts,
      })
      const historyRef = doc(refs.projectHistories(schoolId, projectId))
      transaction.set(historyRef, {
        project: toProjectSnapshotJson({
          ...currentProject,
          status: 'preparing_shipment',
          updated_at: new Date(),
        }),
        updated_by_user_type: 'school',
        updated_by_user_id: schoolId,
        updated_by_name: '学校',
        updated_by_user_document_path: refs.schoolAccount(schoolId).path,
        // 履歴summaryに表示される文言（ユーザーが「郵送開始」を確認できるようにする）
        update_info: '郵送開始を依頼しました',
        created_at: ts,
        updated_at: ts,
        is_deleted: false,
      })
    })

    // 郵送回が未設定のリクエストに今回の郵送回を付与する
    const requestsRef = refs.addTownCompanyRequests(schoolId, projectId)
    const requestsSnap = await getDocs(
      query(requestsRef, where('is_deleted', '==', false)),
    )
    const toUpdate = requestsSnap.docs.filter((d: QueryDocumentSnapshot) => {
      const data = d.data() as { send_request_number?: string | null; status?: string | null }
      const status = data.status
      const isCancelled = status === 'cancelled' || status === 'cancel'
      return !data.send_request_number && !isCancelled
    })
    if (toUpdate.length > 0) {
      const db = getFirestore(getFirebaseApp())
      const batch = writeBatch(db)
      const ts = serverTimestamp()
      toUpdate.forEach((d: QueryDocumentSnapshot) => {
        batch.update(d.ref, {
          send_request_number: formData.requestNumber,
          updated_at: ts,
        })
      })
      await batch.commit()
    }
  }


  /**
   * 事業所回答フォームの回答期限のみを更新する（school / admin 用）。
   */
  async updateFormDeadline(schoolId: string, projectId: string, deadline: Date): Promise<void> {
    const form = await this.getForm(schoolId, projectId)
    if (!form) {
      throw new Error('このプロジェクトに紐づくフォームが見つかりません')
    }
    if (Number.isNaN(deadline.getTime())) {
      throw new Error('回答期限の日付が不正です')
    }
    const formRef = refs.companySurveyForm(schoolId, projectId)
    const deadlineTs = Timestamp.fromDate(deadline)
    await updateWithUpdatedAt(formRef, {
      deadline: deadlineTs,
    } as UpdateData<CompanySurveyFormDoc>)
  }

  /**
   * 回答受付停止：フォームの closed を true にし、プロジェクトステータスを collection_closed に更新（school 用）。
   */
  async stopResponse(schoolId: string, projectId: string, closeReason?: string): Promise<void> {
    const projectSnap = await getDoc(refs.schoolAccountProject(schoolId, projectId))
    if (!projectSnap.exists()) throw new Error('プロジェクトが見つかりません')
    const currentProject = toProjectEntity(projectSnap.id, projectSnap.data() as ProjectDoc, projectSnap.ref.path)
    currentProject.school_account_id = schoolId
    const form = await this.getForm(schoolId, projectId)
    if (!form) throw new Error('このプロジェクトに紐づくフォームが見つかりません')
    const formRef = refs.companySurveyForm(schoolId, projectId)
    const projectRef = refs.schoolAccountProject(schoolId, projectId)
    const db = getFirestore(getFirebaseApp())
    const ts = serverTimestamp()
    const now = new Date()
    const normalizedCloseReason = closeReason?.trim() || null
    await runTransaction(db, async (transaction) => {
      transaction.update(formRef, {
        closed: true,
        close_reason: normalizedCloseReason,
        updated_at: ts,
      } as UpdateData<CompanySurveyFormDoc>)
      transaction.update(projectRef, { status: 'collection_closed', updated_at: ts })
      const historyRef = doc(refs.projectHistories(schoolId, projectId))
      transaction.set(historyRef, {
        project: toProjectSnapshotJson({ ...currentProject, status: 'collection_closed', updated_at: now }),
        updated_by_user_type: 'school',
        updated_by_user_id: schoolId,
        updated_by_name: '学校',
        updated_by_user_document_path: refs.schoolAccount(schoolId).path,
        update_info: '回答受付を停止し、進行状況を「受付終了」に更新',
        created_at: ts,
        updated_at: ts,
        is_deleted: false,
      })
    })
  }

  /**
   * 回答受付再開：フォームの closed を false にし、プロジェクトステータスを collecting_responses に更新（school 用）。
   * 回答期限を過ぎている場合はエラー。
   */
  async resumeResponse(schoolId: string, projectId: string): Promise<void> {
    const projectSnap = await getDoc(refs.schoolAccountProject(schoolId, projectId))
    if (!projectSnap.exists()) throw new Error('プロジェクトが見つかりません')
    const currentProject = toProjectEntity(projectSnap.id, projectSnap.data() as ProjectDoc, projectSnap.ref.path)
    currentProject.school_account_id = schoolId
    const form = await this.getForm(schoolId, projectId)
    if (!form) throw new Error('このプロジェクトに紐づくフォームが見つかりません')
    const now = new Date()
    const formDeadline = form.deadline
    if (!formDeadline || formDeadline < now) {
      throw new Error('回答期限を過ぎているため、受付を再開できません')
    }
    const formRef = refs.companySurveyForm(schoolId, projectId)
    const projectRef = refs.schoolAccountProject(schoolId, projectId)
    const db = getFirestore(getFirebaseApp())
    const ts = serverTimestamp()
    const resumeAt = new Date()
    await runTransaction(db, async (transaction) => {
      transaction.update(formRef, {
        closed: false,
        close_reason: null,
        updated_at: ts,
      } as UpdateData<CompanySurveyFormDoc>)
      transaction.update(projectRef, { status: 'collecting_responses', updated_at: ts })
      const historyRef = doc(refs.projectHistories(schoolId, projectId))
      transaction.set(historyRef, {
        project: toProjectSnapshotJson({ ...currentProject, status: 'collecting_responses', updated_at: resumeAt }),
        updated_by_user_type: 'school',
        updated_by_user_id: schoolId,
        updated_by_name: '学校',
        updated_by_user_document_path: refs.schoolAccount(schoolId).path,
        update_info: '回答受付を再開し、進行状況を「回答受付中」に更新',
        created_at: ts,
        updated_at: ts,
        is_deleted: false,
      })
    })
  }
}
